import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../types/api';
import { logger } from '../utils/logger';
import { StorageKeys, clearBoardScopedStorage } from '../constants/storageKeys';
import { setAuthFailureHandler } from '../services/api';

interface GuestParticipant {
  participantId: string;
  displayName: string;
  retrospectiveId?: string;  // For retro boards
  pokerSessionId?: string;   // For poker sessions
  guestSessionId: string;  // Unique session ID for guest persistence
  token?: string;  // JWT token for guest authentication
  inviteToken?: string;  // Invite token — replayed to regenerate the guest JWT before it expires
  isGuest: true;
}

interface BoardParticipant {
  retroId: string;
  participantId: string;
}

interface AuthContextType {
  user: User | null;
  guest: GuestParticipant | null;
  login: (user: User) => void;
  loginAsGuest: (guest: GuestParticipant) => void;
  updateGuestParticipantId: (newParticipantId: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  getParticipantId: (retroId: string) => string | null;
  setParticipantId: (retroId: string, participantId: string) => void;
  clearParticipantId: (retroId: string) => void;
  // Poker uses the same storage as retro boards (different key naming)
  getPokerParticipantId: (pokerSessionId: string) => string | null;
  setPokerParticipantId: (pokerSessionId: string, participantId: string) => void;
  clearPokerParticipantId: (pokerSessionId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [guest, setGuest] = useState<GuestParticipant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [boardParticipants, setBoardParticipants] = useState<BoardParticipant[]>([]);

  useEffect(() => {
    // Check guest first — sessionStorage is tab-specific, preventing cross-tab auth contamination
    // Guest mode takes priority: skip loading auth user for this tab
    const savedGuest = sessionStorage.getItem(StorageKeys.GUEST);
    if (savedGuest) {
      try {
        const parsedGuest = JSON.parse(savedGuest);
        // Guest must have either retrospectiveId or pokerSessionId
        // Guest must have either retrospectiveId or pokerSessionId
        if (parsedGuest.isGuest && parsedGuest.participantId &&
            (parsedGuest.retrospectiveId || parsedGuest.pokerSessionId)) {
          setGuest(parsedGuest);
          setIsLoading(false);
          return;
        } else {
          sessionStorage.removeItem(StorageKeys.GUEST);
        }
      } catch (error) {
        sessionStorage.removeItem(StorageKeys.GUEST);
      }
    }

    const savedUser = localStorage.getItem(StorageKeys.USER);
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (parsedUser.id && typeof parsedUser.id === 'string' && guidRegex.test(parsedUser.id)) {
          setUser(parsedUser);
        } else {
          localStorage.removeItem(StorageKeys.USER);
        }
      } catch (error) {
        localStorage.removeItem(StorageKeys.USER);
      }
    }

    const savedBoardParticipants = localStorage.getItem(StorageKeys.BOARD_PARTICIPANTS);
    if (savedBoardParticipants) {
      try {
        const parsed = JSON.parse(savedBoardParticipants);
        if (Array.isArray(parsed)) {
          setBoardParticipants(parsed);
        }
      } catch (error) {
        localStorage.removeItem(StorageKeys.BOARD_PARTICIPANTS);
      }
    }

    setIsLoading(false);
  }, []);

  // Cross-tab logout: when another tab removes 'user' from localStorage (logout),
  // clear auth state in this tab too — but only if this tab is an auth user tab.
  // Guest tabs use sessionStorage and must NOT be affected by this event.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === StorageKeys.USER && e.newValue === null) {
        // Ignore in guest tabs — their auth lives in sessionStorage, not localStorage
        if (sessionStorage.getItem(StorageKeys.GUEST)) return;
        setUser(null);
        setBoardParticipants([]);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback((userData: User) => {
    // Tokens are in httpOnly cookies — store only non-sensitive user info
    const userInfo = {
      id: userData.id,
      username: userData.username,
      displayName: userData.displayName,
    };
    setUser(userData);
    setGuest(null);
    localStorage.setItem(StorageKeys.USER, JSON.stringify(userInfo));
    sessionStorage.removeItem(StorageKeys.GUEST);
  }, []);

  const loginAsGuest = useCallback((guestData: GuestParticipant) => {
    setGuest(guestData);
    setUser(null); // In-memory only — don't touch localStorage.user (other tabs still need it)
    sessionStorage.setItem(StorageKeys.GUEST, JSON.stringify(guestData));
  }, []);

  const updateGuestParticipantId = useCallback((newParticipantId: string) => {
    if (!guest) return;

    const updatedGuest = {
      ...guest,
      participantId: newParticipantId
    };

    setGuest(updatedGuest);
    sessionStorage.setItem(StorageKeys.GUEST, JSON.stringify(updatedGuest));
  }, [guest]);

  const logout = useCallback(async () => {
    // Guests authenticate via Bearer token (sessionStorage), NOT httpOnly cookies.
    // Calling ApiService.logout() for a guest tab would delete the owner's httpOnly
    // cookie if both are open in the same browser — do NOT make the API call for guests.
    const currentGuest = sessionStorage.getItem(StorageKeys.GUEST);
    if (!currentGuest) {
      try {
        const { ApiService } = await import('../services/api');
        await ApiService.logout();
      } catch (error) {
        logger.error('Logout API call failed:', error);
      }
    }

    setUser(null);
    setGuest(null);
    setBoardParticipants([]);
    localStorage.removeItem(StorageKeys.USER);
    sessionStorage.removeItem(StorageKeys.GUEST);
    localStorage.removeItem(StorageKeys.BOARD_PARTICIPANTS);
    clearBoardScopedStorage();
  }, []);

  const getParticipantId = useCallback((retroId: string): string | null => {
    const found = boardParticipants.find(bp => bp.retroId === retroId);
    return found?.participantId || null;
  }, [boardParticipants]);

  const setParticipantId = useCallback((retroId: string, participantId: string) => {
    setBoardParticipants(prev => {
      const filtered = prev.filter(bp => bp.retroId !== retroId);
      const updated = [...filtered, { retroId, participantId }];
      // Keep last 20 entries to prevent unbounded localStorage growth
      const trimmed = updated.length > 20 ? updated.slice(updated.length - 20) : updated;
      localStorage.setItem(StorageKeys.BOARD_PARTICIPANTS, JSON.stringify(trimmed));
      return trimmed;
    });
  }, []);

  const clearParticipantId = useCallback((retroId: string) => {
    setBoardParticipants(prev => {
      const updated = prev.filter(bp => bp.retroId !== retroId);
      localStorage.setItem(StorageKeys.BOARD_PARTICIPANTS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const getPokerParticipantId = useCallback((pokerSessionId: string): string | null => {
    return getParticipantId(pokerSessionId);
  }, [getParticipantId]);

  const setPokerParticipantId = useCallback((pokerSessionId: string, participantId: string) => {
    setParticipantId(pokerSessionId, participantId);
  }, [setParticipantId]);

  const clearPokerParticipantId = useCallback((pokerSessionId: string) => {
    clearParticipantId(pokerSessionId);
  }, [clearParticipantId]);

  // Register auth failure handler so api.ts redirects via React Router (not window.location.href)
  useEffect(() => {
    setAuthFailureHandler(() => {
      setUser(null);
      setGuest(null);
      setBoardParticipants([]);
      navigate('/login');
    });
  }, [navigate]);

  const isAuthenticated = !!user;
  const isGuest = !!guest;

  const contextValue = useMemo(() => ({
    user,
    guest,
    login,
    loginAsGuest,
    updateGuestParticipantId,
    logout,
    isAuthenticated,
    isGuest,
    isLoading,
    getParticipantId,
    setParticipantId,
    clearParticipantId,
    getPokerParticipantId,
    setPokerParticipantId,
    clearPokerParticipantId
  }), [
    user,
    guest,
    isAuthenticated,
    isGuest,
    isLoading,
    login,
    loginAsGuest,
    updateGuestParticipantId,
    logout,
    getParticipantId,
    setParticipantId,
    clearParticipantId,
    getPokerParticipantId,
    setPokerParticipantId,
    clearPokerParticipantId
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};